import {
  BRAND_FILTER_QUERY_KEY,
  EMPTY_VALUE,
  REGION_FILTER_QUERY_KEY,
} from "staticConfiguration";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { ErrorComponent } from "core/components/ErrorComponent";
import { PageContainer } from "core/components/PageContainer";
import { PageHeader } from "core/components/PageHeader";
import { QuerySortButton } from "core/components/QueryComponents/QuerySortButton";
import type { TFunction } from "core/entities";
import { useCentrixApiInfinite } from "core/hooks/useCentrixApiInfinite";
import { useIntersectionObserver } from "core/hooks/useIntersectionObserver";
import { useLocale } from "core/hooks/useLocale";
import { usePageLogging } from "core/hooks/usePageLogging";
import { useRegion } from "core/hooks/useRegion";
import { getBrand } from "core/utils/getBrand";
import { FavoriteButton } from "features/MyLinks/components";
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsString,
  useQueryState,
} from "nuqs";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NewsArticlesFilters } from "./NewsArticlesFilters";
import { NewsCard } from "./NewsCard";

function Wrapper({
  t,
  children,
  currentCount,
  totalCount,
}: {
  t: TFunction;
  children: ReactNode;
  currentCount: number;
  totalCount: number;
}) {
  return (
    <PageContainer maxWidth="xl">
      <PageHeader
        title={t("pageTitle")}
        TitleSlot={
          <FavoriteButton
            itemId="all-news"
            itemType="Page"
            title={t("pageTitle")}
          />
        }
      />
      <NewsArticlesFilters sx={{ mb: 4 }} />
      <Typography variant="overline" px={2}>
        {t("sectionTitle", { currentCount, totalCount })}
      </Typography>
      <QuerySortButton
        queryKey="direction"
        defaultDirection="desc"
        ascendingLabel={t("sorting.asc")}
        descendingLabel={t("sorting.desc")}
      />
      <Divider sx={{ mb: 3 }} />
      {children}
    </PageContainer>
  );
}

export function AllNewsPage() {
  usePageLogging({
    itemType: "Page",
    itemId: "all-news",
    componentName: "AllNewsPage",
  });
  const locale = useLocale();

  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:AllNewsPage",
  });
  const defaultBrand = getBrand();
  const defaultRegion = useRegion();
  const [selectedBrands, setSelectedBrands] = useQueryState(
    BRAND_FILTER_QUERY_KEY,
    parseAsArrayOf(parseAsString)
  );
  const [selectedRegions, setSelectedRegions] = useQueryState(
    REGION_FILTER_QUERY_KEY,
    parseAsArrayOf(parseAsString)
  );
  const isFirstRender = useRef(true);
  const [forceDefaultBrandRegion, setForceDefaultBrandRegion] = useQueryState(
    "force-default-brand-region",
    parseAsBoolean
  );
  useEffect(() => {
    if (!isFirstRender.current) {
      return;
    }
    if (selectedBrands === null) {
      setSelectedBrands([defaultBrand.brandCode]);
    }
    if (selectedRegions === null) {
      setSelectedRegions([defaultRegion]);
    }
    isFirstRender.current = false;
  }, [
    defaultBrand.brandCode,
    defaultRegion,
    selectedBrands,
    selectedRegions,
    setSelectedBrands,
    setSelectedRegions,
  ]);
  useEffect(() => {
    if (forceDefaultBrandRegion) {
      setSelectedBrands([defaultBrand.brandCode]);
      setSelectedRegions([defaultRegion]);
      setForceDefaultBrandRegion(null);
    }
  }, [
    defaultBrand.brandCode,
    defaultRegion,
    forceDefaultBrandRegion,
    setForceDefaultBrandRegion,
    setSelectedBrands,
    setSelectedRegions,
  ]);
  const [selectedTags] = useQueryState("tags", parseAsArrayOf(parseAsString));
  const [selectedCategories, setSelectedCategories] = useQueryState(
    "categories",
    parseAsArrayOf(parseAsString)
  );
  const [selectedTypes, setSelectedTypes] = useQueryState(
    "types",
    parseAsArrayOf(parseAsString)
  );
  const {
    items,
    isLoadingInitialData,
    isLoadingMore,
    isError,
    currentCount,
    totalCount,
    loadMore,
  } = useCentrixApiInfinite({
    path: "/api/news-events/articles-v2/News/public",
    parameters: {
      query: {
        Locale: locale ?? "en",
        Brands: selectedBrands ?? [],
        Regions: selectedRegions ?? [],
        Categories: selectedCategories ?? [],
        Types: selectedTypes ?? [],
        Tags: selectedTags ?? [],
      },
    },
    maxResultCount: 10,
    swrOptions: { revalidateFirstPage: false },
  });

  const intersectionCallBack = useCallback<IntersectionObserverCallback>(
    (entries) => {
      const lastEntry = entries.at(-1);
      const shouldLoadMore =
        lastEntry?.isIntersecting && typeof loadMore === "function";
      if (shouldLoadMore) {
        loadMore();
      }
    },
    [loadMore]
  );

  const { ref: intersectionObserverRef } =
    useIntersectionObserver(intersectionCallBack);

  if (isError) {
    return (
      <Wrapper t={t} currentCount={currentCount} totalCount={totalCount}>
        <ErrorComponent apiError={isError} />
      </Wrapper>
    );
  }

  if (isLoadingInitialData) {
    return (
      <Wrapper t={t} currentCount={currentCount} totalCount={totalCount}>
        <LinearProgress />
      </Wrapper>
    );
  }
  return (
    <Wrapper t={t} currentCount={currentCount} totalCount={totalCount}>
      <Box
        component="section"
        sx={{
          marginX: "auto",
          display: "grid",
          justifyContent: "center",
          gap: 2,
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          mb: 2,
        }}
      >
        {items.length === 0 && (
          <Typography ml={1} color="text.secondary">
            {t("noItems")}
          </Typography>
        )}
        {items.map(
          (
            {
              id,
              title,
              blurb,
              banner: articleBanner,
              articleType,
              articleCategory,
              publishedTime,
            },
            index
          ) => (
            <NewsCard
              onChipClick={() => {
                if (articleType?.id) {
                  setSelectedTypes([articleType.id]);
                }
              }}
              key={id}
              id={id ?? ""}
              ref={
                /* applying intersectionObserver ref to 4th last element */
                index === items.length - 5 ? intersectionObserverRef : undefined
              }
              title={title ?? EMPTY_VALUE}
              blurb={blurb ?? EMPTY_VALUE}
              publishedTime={publishedTime ?? EMPTY_VALUE}
              articleType={articleType}
              articleCategory={articleCategory}
              onCategoryClick={() => {
                setSelectedCategories([articleCategory?.id ?? "uncategorized"]);
              }}
              articleBanner={articleBanner}
            />
          )
        )}
      </Box>
      {isLoadingMore && <LinearProgress />}
    </Wrapper>
  );
}
